import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { decryptFromBackend } from "../../../utils/urlDecryptor";
import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessage from "./ChatMessage";
import ConsultationReminderModal from "./ConsultationReminderModal";
import FeedbackScreen from "./FeedbackScreen";
import PatientCard from "./PatientCard";

import { useChatMessages } from "./hooks/useChatMessages";
import { useKeyboardOffset } from "./hooks/useKeyboardOffset";
import { useTeleconsultation } from "./hooks/useTeleconsultation";
import { useZegoChat } from "./hooks/useZegoChat";
import { isSystemMessage } from "./utils/chatHelpers";
import { assetUrl, defaultAvatarUrl, imageUrl } from "../../../utils/assets";

// ─── Styled Components ────────────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh;
  height: 100vh;
  overflow: hidden;
  align-self: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 120px 16px 130px;
  overflow-y: auto;
  background-image: url(${assetUrl("images/bg-chat.png")});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`;

const ChatInputContainer = styled.div`
  position: fixed;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  background: #ffffff;
  padding: 0 0 calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.2s ease;
  width: 100%;
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const Chat: React.FC = () => {
  const { conversationID, token } = useParams<{
    conversationID: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return "";
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error("Failed to decrypt token:", err);
      return "";
    }
  }, [token]);

  // ─── Hooks ────────────────────────────────────────────────────────────────

  const {
    teleconsultationData,
    isExpired,
    showReminder,
    setShowReminder,
    showRating,
    setShowRating,
  } = useTeleconsultation(conversationID, decryptedToken);

  const { isLoggedIn, isGroupReady, userId, userDokter, groupId, ensureGroupReady } =
    useZegoChat(teleconsultationData);

  const { messages, replyTo, setReplyTo, handleSend, handleSendMedia, loadChatHistory, loading: isChatLoading } =
    useChatMessages({
      isLoggedIn,
      groupId,
      userId,
      userDokter,
      conversationID,
      decryptedToken,
      ensureGroupReady,
      isGroupReady,
    });

  const keyboardOffset = useKeyboardOffset();

  // ─── Listen for consultation end event ───────────────────────────────────
  const navigate = useNavigate();
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      navigate('/home', { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  useEffect(() => {
    const handleConsultationEnded = () => {
      setShowRating(true);
    };
    window.addEventListener("consultation:ended", handleConsultationEnded);
    return () =>
      window.removeEventListener("consultation:ended", handleConsultationEnded);
  }, [setShowRating]);

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLDivElement | null>(null);
  const messageRefsMap = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [highlightedMessageID, setHighlightedMessageID] = React.useState<
    string | null
  >(null);

  // ─── Scroll Management ─────────────────────────────────────────────────────

  const handleScroll = useCallback(async () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop } = messagesContainerRef.current;

    // Trigger load more when user scrolls to the top (threshold 50px)
    if (scrollTop <= 50 && !isChatLoading) {
      console.log("⬆️ Loading older messages...");

      // Save current scroll height to adjust after loading
      const previousHeight = messagesContainerRef.current.scrollHeight;

      await loadChatHistory(null);

      // Adjust scroll position so the user doesn't jump to the top
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const newHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = newHeight - previousHeight;
        }
      });
    }
  }, [isChatLoading, loadChatHistory]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, keyboardOffset, scrollToBottom]);

  // ─── Reply quote click ────────────────────────────────────────────────────

  const handleReplyQuoteClick = (messageID: string) => {
    setHighlightedMessageID(messageID);
    const messageElement = messageRefsMap.current[messageID];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => setHighlightedMessageID(null), 2000);
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  if (!teleconsultationData) {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        Memuat data telekonsultasi...
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <ChatContainer>
      <ChatHeader
        doctorAvatar={
          teleconsultationData.practitioner_avatar_url ||
          "https://m.linksehat.com/assets/img/users/female-avatar.png"
        }
        doctorName={teleconsultationData.practitioner_name || "Dokter"}
        doctorSpecialty={
          teleconsultationData.practitioner_speciality || "Spesialis Umum"
        }
        consultationMedia={teleconsultationData.teleconsultation_type || "CHAT"}
        startTime={teleconsultationData.teleconsultation_start_time}
        duration={teleconsultationData.teleconsultation_duration_timeout}
        isExpired={isExpired}
        consultationId={teleconsultationData.teleconsultation_id || ""}
        token={decryptedToken}
        serverTime={teleconsultationData.server_time}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "12px 0",
          boxShadow: "2px 2px 2px rgba(0,0,0,0.1)",
          zIndex: 100,
        }}
      >
        <PatientCard
          name={teleconsultationData.patient_name || "Nama Pasien"}
          insurance={teleconsultationData.patient_insurance || "Insurance"}
        />
      </div>

      <MessagesContainer
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {isChatLoading && messages.length === 0 && (
          <div
            style={{
              margin: "auto",
              padding: "8px 16px",
              borderRadius: 16,
              background: "rgba(255,255,255,0.85)",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            Memuat percakapan...
          </div>
        )}

        {messages
          .filter(
            (msg) =>
              typeof msg.message === "string" && !isSystemMessage(msg.message),
          )
          .map((msg) => (
            <ChatMessage
              key={`${msg.messageID}-${msg.timestamp}`}
              {...msg}
              onReply={() => setReplyTo(msg as any)}
              doctorName={teleconsultationData.practitioner_name || "Dokter"}
              doctorSpecialty={
                teleconsultationData.practitioner_speciality || "Spesialis Umum"
              }
              avatar={imageUrl(teleconsultationData.practitioner_avatar_url, defaultAvatarUrl())}
              userDokter={userDokter}
              patientName={teleconsultationData.patient_name}
              ref={(element: any) => {
                if (element) messageRefsMap.current[msg.messageID] = element;
              }}
              isHighlighted={highlightedMessageID === msg.messageID}
              onReplyQuoteClick={handleReplyQuoteClick}
              replyTo={msg.replyTo}
            />
          ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {!isExpired && (
        <ChatInputContainer
          ref={chatInputRef}
          style={{ bottom: keyboardOffset }}
        >
          <ChatInput
            onSend={handleSend}
            onSendMedia={handleSendMedia}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            doctorName={teleconsultationData.practitioner_name || "Dokter"}
            isDoctor={false}
          />
        </ChatInputContainer>
      )}

      <ConsultationReminderModal
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />

      <FeedbackScreen
        doctorId={teleconsultationData.practitioner_id || ""}
        teleconsultId={teleconsultationData.teleconsultation_id || ""}
        teleconsultationCode={teleconsultationData.teleconsultation_code || ""}
        patientId={teleconsultationData.patient_id || ""}
        token={decryptedToken}
        visible={showRating}
        onClose={() => setShowRating(false)}
      />
    </ChatContainer>
  );
};

export default Chat;
